package frontend.pages

import androidx.compose.runtime.*
import net.sergeych.ugdx.*
import components.*
import frontend.*
import frontend.components.*
import frontend.lib.*
import frontend.models.Address
import frontend.models.User
import kotlinx.browser.window
import kotlinx.coroutines.GlobalScope
import kotlinx.coroutines.launch
import net.sergeych.mp_logger.Log
import net.sergeych.parsec.connectBrowserConsole
import org.jetbrains.compose.web.attributes.disabled
import org.jetbrains.compose.web.dom.*
import wrappers.toChecksumAddress

enum class Step {
    Select,
    Confirm
}

class AccountScreenContext(app: App) {
    var showAttachModal by mutableStateOf(false)
    val app = app

//    fun removeAccount(account: ExternalAccount) {
////        app.removeAccount(account)
//    }
}

class AccountAttachContext(val user: User, val onDone: (account: Address) -> Unit) {
    var address by mutableStateOf("")
    var name by mutableStateOf("")
    var confirmationText by mutableStateOf("")
    var step by mutableStateOf(Step.Select)

    suspend fun requestConfirmation(_account: String, _name: String) {
        address = _account
        name = _name
        confirmationText = EthereumAdapter.signatureMessageForAddress(address)
        step = Step.Confirm
    }

    suspend fun confirm(signature: String) {
        return user.ethereum.addSignedAddress(name, address, confirmationText, signature)
//        app.addAccount(account)
//        onDone(EthAccount(name, address))
    }
}

@Composable
fun SelectStep(context: AccountAttachContext) {
    val scope = rememberCoroutineScope()
    var isLoading: Boolean by mutableStateOf(false)
    var metamaskError: String by mutableStateOf("")
    var addressError: String by mutableStateOf("")
    var requestError: String by mutableStateOf("")
    var account: String by mutableStateOf("")
    var name: String by mutableStateOf("")

    fun validateAddress() {
        addressError = if (!isETHAddress(account)) "Address is invalid" else ""
        if (context.user.addresses.find{ it.address == account } != null) addressError = "This account is already attached"
    }

    fun validateName() {
        addressError = if (name == "") "Name can not be empty" else ""
    }

    fun enableMetamask() {
        isLoading = true
        metamaskError = ""

        scope.launch {
            try {
                val accounts = MetaMask.getAccounts()
                if (accounts.size > 0) account = toChecksumAddress(accounts[0])
                validateAddress()
            } catch (err: dynamic) {
                metamaskError = err.message
            }

            isLoading = false
        }
    }

    fun requestConfirmation() {
        isLoading = true

        scope.launch {
            try {
                context.requestConfirmation(account, name)
                isLoading = false
            } catch (err: dynamic) {
                println(err)
                requestError = err
            }
        }
    }

    @Composable
    fun customLabel() {
        if (isMetaMaskAvailable()) {
            Text("Provide your account or ")
            A("javascript:void(0);", attrs = {
                classes("font-medium", "text-indigo-600", "hover:text-indigo-500")
                onClick { enableMetamask() }
            }) {
                Text("connect via MetaMask")
            }
        } else {
            Text("Provide your account")
        }
    }

    TWForm() {
        H3(attrs = { classList("text-lg leading-6 font-medium text-gray-900") }) {
            Text("Attach new ETH account")
        }

        TTextInput("account", account) {
            label { customLabel() }
            onInput {
                account = it
                validateAddress()
            }

            onChange { validateAddress() }
        }

        TTextInput("name", name) {
            label { Text("Account name") }
            onInput { name = it }

            onChange { validateName() }
        }
        if (metamaskError !== "") P(attrs = { classes("text-red-600") }) {
            Text(metamaskError)
        }
        if (addressError != "") P(attrs = { classes("text-red-600") }) {
            Text(addressError)
        }
        if (account != "" && addressError == "" && name != "") TWSubmitButton("Continue", isLoading = isLoading) {
            requestConfirmation()
        }
    }
}

@Composable
fun AccountAttachModal(user: User, onClose: (acc: Address?) -> Unit) {
    val context = AccountAttachContext(user, onClose)

    Modal(onClose = { onClose(null) }) {
        if (context.step == Step.Select) SelectStep(context)
        if (context.step == Step.Confirm) ConfirmStep(context)
    }
}

@Composable
fun ConfirmStep(context: AccountAttachContext) {
    val scope = rememberCoroutineScope()
    var signature by mutableStateOf("")
    var metaMaskError by mutableStateOf("")
    var confirmationError by mutableStateOf("")
    var isClipboardLoading by mutableStateOf(false)
    var clipboardButtonLabel by mutableStateOf("Copy to clipboard")

    @Composable
    fun clipboardLabel() {
        ClipboardCopyIcon("-ml-1 mr-3 h-5 w-5")
        Text(clipboardButtonLabel)
    }

    fun copyToClipboard() {
        isClipboardLoading = true

        scope.launch {
            try {
                window.navigator.clipboard.writeText(context.confirmationText).await()
            } catch(err: dynamic) {
                println(err)
            }

            clipboardButtonLabel = "Copied!"
            isClipboardLoading = false
        }
    }

    fun signMetamask() {
        metaMaskError = ""

        scope.launch {
            try {
                signature = MetaMask.sign(textToHex(context.confirmationText), context.address)
            } catch (e: dynamic) {
                metaMaskError = e.message
            }
        }
    }

    fun confirm() {
        GlobalScope.launch {
            try {
                context.confirm(signature)
                println("confirm done")
                context.user.addAddress(Address(context.name, context.address))
                println("create eth account done")
                context.onDone(Address(context.name, context.address))
            } catch (e: EthereumError) {
                println("ETH ERR $e")
                when(e) {
                    is AddressInUseError -> confirmationError = "this address is already tracked by other user"
                    is InvalidOwnershipSignature -> confirmationError = "bad signature"
                    is InvalidOwnershipSignedMessage -> confirmationError = "bad (changed?) message"
                }
            } catch (e: Throwable) {
                println("SOME ERR: $e")
            }
        }
    }

    @Composable
    fun customLabel() {
        if (!isMetaMaskAvailable()) return Text("Please, sign this text manually")

        Text("Please, sign this text manually or ")
        A("javascript:void(0);", attrs = {
            classes("font-medium", "text-indigo-600", "hover:text-indigo-500")
            onClick { signMetamask() }
        }) { Text("sign with MetaMask") }
        if (metaMaskError != "") P(attrs = { classes("text-red-600") }) { Text(metaMaskError) }
    }

    TWForm() {
        H3(attrs = { classList("text-lg leading-6 font-medium text-gray-900") }) {
            Text("Confirm new ETH account")
        }
        TWTextArea("signIt", { customLabel() }, context.confirmationText) {
            disabled()
        }

        TWButton({ clipboardLabel() }, isLoading = isClipboardLoading) { copyToClipboard() }
        TWTextArea("signature", "Paste your signature here", signature) {
            onInput { signature = it.value }
        }

        if (confirmationError != "") P(attrs = { classes("text-red-600") }) { Text(confirmationError) }
        if (signature != "") TWButton("Confirm") {
            confirm()
        }
    }
}


